<div class="margin-head">
  <div>
    <h4>{{ 'tokenAnalytics.margin.title' | translate }}</h4>
    <p class="page-hint">
      {{ 'tokenAnalytics.margin.subtitle' | translate }}
      <ng-container *ngIf="report?.window_start as from">
        · {{ 'tokenAnalytics.margin.dataFrom' | translate: { date: (from | date: 'dd.MM.yyyy') } }}
      </ng-container>
    </p>
  </div>
  <div class="controls">
    <nb-button-group size="small" (valueChange)="setDays($event[0])">
      <button nbButtonToggle *ngFor="let d of dayPresets" [value]="d" [pressed]="days === d">
        {{ 'tokenAnalytics.window.days' | translate: { days: d } }}
      </button>
    </nb-button-group>
    <button nbButton status="primary" size="small" (click)="importNow()"
            [disabled]="importing" [nbSpinner]="importing" nbSpinnerSize="tiny">
      <nb-icon icon="download-outline"></nb-icon>
      {{ 'tokenAnalytics.margin.import' | translate }}
    </button>
  </div>
</div>

<p class="page-hint" *ngIf="loadFailed">{{ 'tokenAnalytics.margin.loadError' | translate }}</p>

<div [nbSpinner]="isLoading" nbSpinnerStatus="primary" *ngIf="report as r">
  <div class="kpi-grid">
    <app-kpi-card [label]="'tokenAnalytics.margin.kpi.revenue' | translate"
                  [value]="r.total.revenue_eur" [formatFunction]="euro" [change]="null"
                  icon="flash-outline"></app-kpi-card>
    <app-kpi-card [label]="'tokenAnalytics.margin.kpi.cost' | translate"
                  [value]="r.total.cost_eur" [formatFunction]="euro" [change]="null"
                  icon="pricetags-outline"></app-kpi-card>
    <app-kpi-card [label]="'tokenAnalytics.margin.kpi.margin' | translate"
                  [value]="r.total.margin_pct ?? 0" [formatFunction]="pct" [change]="null"
                  [unit]="'tokenAnalytics.margin.kpi.target' | translate"
                  icon="trending-up-outline"></app-kpi-card>
    <app-kpi-card [label]="'tokenAnalytics.margin.kpi.overhead' | translate"
                  [value]="r.total.overhead_share_pct ?? 0" [formatFunction]="pct" [change]="null"
                  [unit]="euro(r.total.overhead_eur)"
                  icon="layers-outline"></app-kpi-card>
  </div>

  <p class="page-hint" *ngIf="r.total.unpriced_runs > 0">
    ⚠️ {{ 'tokenAnalytics.margin.unpriced' | translate: { n: r.total.unpriced_runs } }}
  </p>

  <nb-card>
    <nb-card-header><h6><nb-icon icon="list-outline"></nb-icon>{{ 'tokenAnalytics.margin.byActivity' | translate }}</h6></nb-card-header>
    <nb-card-body>
      <table class="m-table">
        <thead>
          <tr>
            <th>{{ 'tokenAnalytics.margin.col.activity' | translate }}</th>
            <th class="num">{{ 'tokenAnalytics.margin.col.uses' | translate }}</th>
            <th class="num">{{ 'tokenAnalytics.margin.col.revenue' | translate }}</th>
            <th class="num">{{ 'tokenAnalytics.margin.col.cost' | translate }}</th>
            <th class="num">{{ 'tokenAnalytics.margin.col.margin' | translate }}</th>
            <th class="num">{{ 'tokenAnalytics.margin.col.costPerUse' | translate }}</th>
            <th class="num" [nbTooltip]="'tokenAnalytics.margin.col.creditsFor80Hint' | translate">
              {{ 'tokenAnalytics.margin.col.creditsFor80' | translate }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let a of r.activities; trackBy: trackByKey">
            <td>{{ a.label }}</td>
            <td class="num">{{ a.uses | number }}</td>
            <td class="num">{{ euro(a.revenue_eur) }}</td>
            <td class="num">{{ euro(a.cost_eur) }}</td>
            <td class="num"><span class="lvl lvl--{{ level(a.margin_pct) }}">{{ a.margin_pct === null ? '—' : pct(a.margin_pct) }}</span></td>
            <td class="num">{{ a.cost_per_use_eur === null ? '—' : (a.cost_per_use_eur | number: '1.4-4') + ' €' }}</td>
            <td class="num">{{ a.credits_for_80 === null ? '—' : (a.credits_for_80 | number: '1.0-2') }}</td>
          </tr>
        </tbody>
      </table>
    </nb-card-body>
  </nb-card>

  <div class="two-col">
    <nb-card>
      <nb-card-header><h6><nb-icon icon="briefcase-outline"></nb-icon>{{ 'tokenAnalytics.margin.byClient' | translate }}</h6></nb-card-header>
      <nb-card-body>
        <table class="m-table">
          <thead>
            <tr>
              <th>{{ 'tokenAnalytics.margin.col.client' | translate }}</th>
              <th class="num">{{ 'tokenAnalytics.margin.col.revenue' | translate }}</th>
              <th class="num">{{ 'tokenAnalytics.margin.col.cost' | translate }}</th>
              <th class="num">{{ 'tokenAnalytics.margin.col.margin' | translate }}</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let c of r.clients; trackBy: trackByKey">
              <td class="clip">{{ c.label }}</td>
              <td class="num">{{ euro(c.revenue_eur) }}</td>
              <td class="num">{{ euro(c.cost_eur) }}</td>
              <td class="num"><span class="lvl lvl--{{ level(c.margin_pct) }}">{{ c.margin_pct === null ? '—' : pct(c.margin_pct) }}</span></td>
            </tr>
          </tbody>
        </table>
      </nb-card-body>
    </nb-card>

    <nb-card>
      <nb-card-header><h6><nb-icon icon="layers-outline"></nb-icon>{{ 'tokenAnalytics.margin.overhead' | translate }}</h6></nb-card-header>
      <nb-card-body>
        <table class="m-table">
          <thead>
            <tr>
              <th>{{ 'tokenAnalytics.margin.col.source' | translate }}</th>
              <th class="num">{{ 'tokenAnalytics.margin.col.cost' | translate }}</th>
              <th class="num">{{ 'tokenAnalytics.margin.col.tokens' | translate }}</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let o of r.overhead; trackBy: trackByKey" [class.unbilled]="o.unbilled_turn">
              <td class="clip">{{ o.source }}</td>
              <td class="num">{{ euro(o.cost_eur) }}</td>
              <td class="num">{{ o.tokens | number }}</td>
            </tr>
          </tbody>
        </table>
        <p class="note">{{ 'tokenAnalytics.margin.overheadNote' | translate }}</p>
      </nb-card-body>
    </nb-card>
  </div>
</div>
